/**
 * Converters for event regulations (HTML <-> JSON)
 */

/**
 * HTML transformations for cleaning formatting
 * Each transformation follows the pattern: { pattern, replacement }
 */
const HTML_CLEANING_RULES = [
  {
    pattern: /<span([^>]*?)font-weight:\s*bold([^>]*?)>(.*?)<\/span>/gi,
    replacement: '<b>$3</b>'
  },
  {
    pattern: /<span([^>]*?)font-style:\s*italic([^>]*?)>(.*?)<\/span>/gi,
    replacement: '<i>$3</i>'
  },
  {
    pattern: /<span([^>]*?)text-decoration:\s*underline([^>]*?)>(.*?)<\/span>/gi,
    replacement: '<u>$3</u>'
  },
  {
    pattern: /<span([^>]*?)style="[^"]*?font-size:[^"]*?"([^>]*?)>(.*?)<\/span>/gi,
    replacement: '$3'
  },
  {
    pattern: /<span[^>]*?>(.*?)<\/span>/gi,
    replacement: '$1'
  },
  {
    pattern: /<br\s*\/?>\s*$/gi,
    replacement: ''
  }
]

/**
 * Applies a transformation rule to the HTML
 * @param {string} html - HTML to be transformed
 * @param {Object} rule - Rule with pattern and replacement
 * @returns {string} Transformed HTML
 */
const applyHtmlCleaningRule = (html, { pattern, replacement }) => {
  return html.replaceAll(pattern, replacement)
}

/**
 * Cleans HTML by removing spans and unnecessary formatting
 * Applies a series of transformations following the Strategy pattern
 * @param {string} html - HTML to be cleaned
 * @returns {string} Clean and normalized HTML
 */
const cleanHtml = (html) => {
  const cleaned = HTML_CLEANING_RULES.reduce((currentHtml, rule) => applyHtmlCleaningRule(currentHtml, rule), html)

  return cleaned.trim()
}

/**
 * Creates the default regulation title
 * @param {string} eventName - Event name
 * @returns {string} Formatted title
 */
const createTitle = (eventName) => {
  return `REGULAMENTO E REGRAS GERAIS BENEFÍCIO ${eventName.toUpperCase()} - PROGRAMA FIDELIDADE BEAUTYBOX`
}

/**
 * Creates the final result with title and questions
 * @param {string} eventName - Event name
 * @param {Array} questions - Array of questions
 * @param {string} eventId - Event ID (optional)
 * @returns {Object} Structured result
 */
const createResult = (eventName, questions, eventId) => {
  const result = {
    title: createTitle(eventName),
    questions
  }
  if (eventId) {
    result.eventId = eventId
  }
  return result
}

/**
 * Processes HTML without divs (using line breaks)
 * @param {string} html - HTML to be processed
 * @param {string} eventName - Event name
 * @param {string} eventId - Event ID
 * @returns {Object} Structured result
 */
const processHtmlWithoutDivs = (html, eventName, eventId) => {
  const parts = html.split(/<br\s*\/?>/gi)
  const questions = parts
    .map((part) => part.trim())
    .filter(Boolean)
    .map((trimmed) => ({ text: cleanHtml(trimmed) }))

  const finalQuestions = questions.length === 0 && html.trim() ? [{ text: cleanHtml(html) }] : questions

  return createResult(eventName, finalQuestions, eventId)
}

/**
 * Processes HTML with divs
 * @param {string} html - HTML to be processed
 * @param {string} eventName - Event name
 * @param {string} eventId - Event ID
 * @returns {Object} Structured result
 */
const processHtmlWithDivs = (html, eventName, eventId) => {
  const divPattern = /<div>(.*?)<\/div>/gi
  const matches = []
  let match

  while ((match = divPattern.exec(html)) !== null) {
    matches.push(match[1])
  }

  const questions = matches
    .filter((content) => content !== '<br>' && content !== '' && content.trim() !== '')
    .map((content) => ({ text: cleanHtml(content) }))

  const finalQuestions = questions.length === 0 && html.trim() ? [{ text: cleanHtml(html) }] : questions

  return createResult(eventName, finalQuestions, eventId)
}

/**
 * Converts HTML to structured JSON
 * Respects exactly the line breaks from contentEditable
 * @param {string} html - HTML to be converted
 * @param {string} eventName - Event name for the title
 * @param {string} eventId - Event ID (optional)
 * @returns {Object} Structured JSON with title and questions
 */
const convertHtmlToJson = (html, eventName = '', eventId = null) => {
  return html.includes('<div>')
    ? processHtmlWithDivs(html, eventName, eventId)
    : processHtmlWithoutDivs(html, eventName, eventId)
}

/**
 * Converts HTML content to API JSON format
 * @param {string} htmlContent - Regulation HTML content
 * @param {string} eventName - Event name
 * @param {string} eventId - Event ID (optional)
 * @returns {Object} Structured JSON
 */
export const convertFromHTMLtoJson = (htmlContent, eventName = '', eventId = null) => {
  const content = htmlContent || ''
  return convertHtmlToJson(content, eventName, eventId)
}

/**
 * Converts API JSON to HTML for display in contentEditable
 * @param {Object} jsonData - JSON with structure { eventId?, title, questions: [{text}] }
 * @returns {string} Formatted HTML for display
 */
export const convertFromJsonToHTML = (jsonData) => {
  if (!jsonData?.questions || !Array.isArray(jsonData.questions)) {
    return ''
  }

  const htmlParts = jsonData.questions.map((q) => q?.text || '').filter((text) => text.trim())

  return htmlParts.join('<div><br></div>')
}

/**
 * Checks if all FAQ items are empty
 * @param {Array} faqArray - Array of objects { question, answer }
 * @returns {Boolean} True if all items are empty, false otherwise
 */
export const isAllEmptyFaq = (faqArray) => faqArray?.every((item) => item?.answer === '' && item?.question === '')

/**
 * Converts FAQ array from form to API format
 * @param {Array} faqArray - Array of objects { question, answer }
 * @returns {Array} Array in API format with { id, title, description }
 */
export const convertFaqToApiFormat = (faqArray) => {
  if (!Array.isArray(faqArray) || isAllEmptyFaq(faqArray)) {
    return ''
  }

  return faqArray
    .filter((item) => item.question && item.answer)
    .map((item, index) => ({
      id: `item-${['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'][index] || index + 1}`,
      title: item.question,
      description: item.answer
    }))
}

/**
 * Converts FAQ array from API to form format
 * @param {Array} apiFaqArray - Array of objects { id, title, description } or old format { pergunta, resposta }
 * @returns {Array} Array in form format with { question, answer }
 */
export const convertFaqFromApiFormat = (apiFaqArray) => {
  if (!Array.isArray(apiFaqArray) || apiFaqArray.length === 0) {
    return [{ question: '', answer: '' }]
  }

  return apiFaqArray.map((item) => ({
    question: item.title || item.pergunta || item.question || '',
    answer: item.description || item.resposta || item.answer || ''
  }))
}
